import { homedir } from 'node:os';
import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import { pathToFileURL } from 'node:url';
import type { HooksConfig } from './hooks-config';
import type { McpServerConfig } from '../src/mcp.client';

/**
 * File-based agent config. Resolved with precedence:
 *   CLI flags  >  ./.agent/config.*  (project)  >  ~/.agent/config.*  (user)  >  built-in defaults.
 * Authored as a TS/JS module (`export default {…}`) or plain JSON. Malformed files are
 * warned about and ignored — config never crashes a run.
 */
export interface AgentConfig {
  model?: string;
  /** Duplex reflex (fast voice) model — used by --duplex/--voice; --voice-model flag wins. */
  reflexModel?: string;
  /** Duplex think (premium reasoning) model. Set to `false` to disable the Think tier. */
  thinkModel?: string | false;
  /** Duplex worker UI chrome: 'full' = per-step ⚙ tool activity, 'minimal' = task events only.
   *  Default 'full' (text duplex) / 'minimal' (--voice); /workers toggles it live. */
  workerChrome?: 'full' | 'minimal';
  /** --voice permission asks: 'menu' (default — interactive Allow once/always/Deny picker, like a
   *  non-voice session) | 'relay' (spoken through the conversation, answer by voice or typing). */
  voiceAskUi?: 'relay' | 'menu';
  maxSteps?: number;
  /** Reasoning/thinking effort: 'off'|'low'|'medium'|'high' or a raw token budget (anthropic/openai). */
  reasoning?: import('../src/index').ReasoningEffort;
  /** Initial permission posture (Shift+Tab cycles it live): default (ask) | acceptEdits | plan. */
  permissionMode?: 'default' | 'acceptEdits' | 'plan';
  /** Line-editor keymap: 'normal' (emacs/readline, default) | 'vim' (modal). */
  editorMode?: 'normal' | 'vim';
  /** Built-in tool set (REPLACES the default set, not additive; WebFetch+WebSearch always added on top).
   *  Names: bash, Read, Edit, Write, Grep, Glob, MultiEdit, ApplyEdits, RepoMap, TodoWrite, WebFetch,
   *  WebSearch, WebSearchAnthropic. */
  tools?: string[];
  /** Anthropic-native web search (`WebSearchAnthropic`): on by default whenever an Anthropic API key
   *  resolves. It bills the Anthropic API account per call (no subscription path) — set `false` to opt out. */
  anthropicWebSearch?: boolean;
  stream?: boolean;
  subagents?: boolean;
  planMode?: boolean;
  // --- providers (merged with env keys; env wins) ---
  /** Per-provider API keys, e.g. `{ openai: 'sk-…', anthropic: 'sk-ant-…' }`. */
  apiKeys?: Record<string, string>;
  /** Per-provider custom base URLs (proxies / self-hosted gateways). */
  baseUrls?: Record<string, string>;
  /** Declarative shell hooks (preToolUse/postToolUse/onStop) — gate or audit tool calls. */
  hooks?: HooksConfig;
  /** Persisted permission rules (CC-style). Each entry is `Tool` or `Tool(pathGlob)`, e.g.
   *  `Edit(src/**)`, `Read(./.env)`, `Shell`. `deny` wins over `allow` wins over `ask`; unmatched
   *  tools fall through to the run's default posture (ask when interactive, allow when unattended). */
  permissions?: { allow?: string[]; ask?: string[]; deny?: string[] };
  /** MCP servers to auto-mount (Claude-style): `{ name: { command, args } | { url } }`. */
  mcpServers?: Record<string, McpServerConfig>;
  // --- kill-switches (raise/lower the always-on safety ceilings) ---
  maxTokens?: number;
  timeoutMs?: number;
  maxRepeats?: number;
  maxToolCalls?: number;
  // --- context shaping (hot/cold) ---
  /** Keep the most-recent N tool-result outputs verbatim; stub older ones (default 8). 0 = keep all. */
  keepToolOutputs?: number;
  /** Token-aware per-turn context ceiling (~4 chars/token). 0/undefined = off. */
  maxContextTokens?: number;
  /** Adaptive trimming that engages only once a browser MCP tool is actually used (huge DOM/screenshot
   *  results blow the cumulative maxTokens budget mid-browse). `resultBytes` caps each browser result;
   *  `keepOutputs` keeps only the most-recent N browser bodies verbatim. Overrides the built-in default. */
  browserTrim?: { resultBytes?: number; keepOutputs?: number };
  /** Auto-capture recurring tool failures as memory lessons (needs .agent/memory). Default off — writes files. */
  learnFromMistakes?: boolean;
  /** On a failed/wasteful headless run (loop/budget/max_steps), reflect once via the model and persist a novel lesson. Default off — costs one model call. */
  reflectOnFailure?: boolean;
  /** Check for updates on startup. Default true; set to false to disable (--no-update-check). */
  updateCheck?: boolean;
  /** Server-side proxy budget caps (USD). Consumed by web/server.ts. */
  budget?: { perSession?: number; global?: number; windowMs?: number };
}

const FILES = ['config.ts', 'config.js', 'config.mjs', 'config.json'];

/** Load the first matching `.agent/config.*` under `dir` (returns {} if none/unreadable). */
async function loadFrom(dir: string): Promise<Partial<AgentConfig>> {
  for (const f of FILES) {
    const p = join(dir, '.agent', f);
    if (!existsSync(p)) continue;
    try {
      const mod = await import(pathToFileURL(p).href, f.endsWith('.json') ? { with: { type: 'json' } } : undefined);
      return (mod.default ?? mod.config ?? mod) as AgentConfig;
    } catch (e: any) {
      console.error(`agentx: ignoring ${p} — ${e?.message ?? e}`);
    }
    return {};
  }
  return {};
}

/**
 * Load `.agent/settings.json` (CC-compatible format). Maps CC fields to AgentConfig:
 *   mcpServers, permissions, env (→ apiKeys where recognized).
 * Returns {} if missing/unreadable.
 */
function loadSettings(dir: string): Partial<AgentConfig> {
  const p = join(dir, '.agent', 'settings.json');
  if (!existsSync(p)) return {};
  try {
    const raw = JSON.parse(readFileSync(p, 'utf8'));
    const cfg: Partial<AgentConfig> = {};
    if (raw.mcpServers && typeof raw.mcpServers === 'object') cfg.mcpServers = raw.mcpServers;
    if (raw.permissions && typeof raw.permissions === 'object') cfg.permissions = raw.permissions;
    if (raw.model && typeof raw.model === 'string') cfg.model = raw.model;
    if (raw.reasoning != null) cfg.reasoning = raw.reasoning;
    if (raw.permissionMode === 'default' || raw.permissionMode === 'acceptEdits' || raw.permissionMode === 'plan') cfg.permissionMode = raw.permissionMode;
    if (raw.editorMode === 'normal' || raw.editorMode === 'vim') cfg.editorMode = raw.editorMode;
    if (typeof raw.stream === 'boolean') cfg.stream = raw.stream;
    if (raw.env && typeof raw.env === 'object') {
      for (const [k, v] of Object.entries(raw.env)) {
        if (typeof v === 'string' && !process.env[k]) process.env[k] = v;
      }
    }
    return cfg;
  } catch (e: any) {
    console.error(`agent: ignoring ${p} — ${e?.message ?? e}`);
    return {};
  }
}

/**
 * Merge config layers (later wins):
 *   ~/.agent/settings.json → ~/.agent/config.* → ./.agent/settings.json → ./.agent/config.*
 */
export async function loadConfig(cwd: string): Promise<Partial<AgentConfig>> {
  const userSettings = loadSettings(homedir());
  const user = await loadFrom(homedir());
  const projectSettings = loadSettings(cwd);
  const project = await loadFrom(cwd);
  const merged = { ...userSettings, ...user, ...projectSettings, ...project };
  // deep-merge mcpServers (all layers contribute servers, not just the last one with the key)
  if (userSettings.mcpServers || user.mcpServers || projectSettings.mcpServers || project.mcpServers) {
    merged.mcpServers = { ...userSettings.mcpServers, ...user.mcpServers, ...projectSettings.mcpServers, ...project.mcpServers };
  }
  return merged;
}
